// @givmo/board — Board members, as a director reads it (CBG-UX10, D-CBG-40; frame c5).
//
// The officer's Board members page without what only officers see or do: its table of who serves —
// the member, whether they are a director, their titles — by name, with the officer page's count
// under it, and the person's own row marked "You" with their own term. No addresses, no other
// person's term, no joining status, no ⋯ menu, no "Add a member". The board's recorded duties follow,
// "Who does what", as About the board shows them.
//
// One read, About the board's (`…/about-the-board`), under the key About reads it by: who serves and
// in what capacity (no addresses), and the duties the board adopted for its officers. The person's
// own term is their seat's, from `/me`. Nothing here is stored or composed by an AI.
//
// A seat for someone who isn't on the board has no Board members (the page is the board's): here by
// address, it meets the one not-found, as About the board does (CBG-YR6-OUTSIDE).

import { useQuery } from '@tanstack/react-query';
import type { BoardDirectorAboutPersonOut } from '@givmo/api-client';
import {
  Badge,
  Card,
  CardBody,
  DataTable,
  Skeleton,
  type DataTableColumn,
} from '@givmo/design-system';
import { useBoardDirectorOperations } from '../../board-runtime';
import { directorBoardIdentity, useDirectorBoardAddress } from '../../director/director-board';
import { isCommitteeOnly } from '../../director/director-wire';
import {
  DirectorReadRefusal,
  isSigningRefusal,
  useDirectorReadsMayFire,
} from '../../director/read-gate';
import { DirectorReadFailed } from '../../director/access-refusal';
import { displayLongDate, todayAsDateInput } from '../../lib/display';
import { extractAbout } from '../onboarding/onboarding-decode';
import { onboardingWords as gs } from '../onboarding/onboarding-words';
import { dutyWords } from '../roster/officer-duties';
import { officerRolesWords as rw } from '../roster/officer-roles-words';
import { AllYourBoards } from './all-your-boards';
import { DirectorBoardContent } from './director-board-content';
import { DirectorBoardOnlyPage } from './director-board-only-page';
import { directorPagesWords as w } from './director-pages-words';
import { DirectorShell } from './director-shell';
import { ownTermLine } from './my-boards-page';

export function DirectorMembersPage() {
  const { board } = useDirectorBoardAddress();
  if (isCommitteeOnly(board)) return <DirectorBoardOnlyPage testId="page-director-members" />;
  return <DirectorMembersPageForTheBoard />;
}

function DirectorMembersPageForTheBoard() {
  const operations = useBoardDirectorOperations();
  const { orgRef, board, notYours, me, settled } = useDirectorBoardAddress();
  const mayFire = useDirectorReadsMayFire(orgRef);
  const aboutQuery = useQuery({
    queryKey: ['board-director-about', orgRef ?? ''],
    enabled: orgRef !== null && mayFire,
    retry: false,
    queryFn: async () => {
      const { data } = await operations.directorGetBoardAbout({
        path: { org_ref: orgRef ?? '' },
        throwOnError: true,
      });
      return extractAbout(data);
    },
  });
  const about = aboutQuery.data ?? null;
  // The officer roster's order: by name.
  const people = [...(about?.people ?? [])].sort((left, right) =>
    (left.display_name ?? '').localeCompare(right.display_name ?? ''),
  );
  const directors = people.filter((person) => person.is_director).length;
  const ownTerm = board === null ? null : ownTermLine(board, todayAsDateInput());
  const titles = about?.duties?.titles ?? [];

  const columns: DataTableColumn<BoardDirectorAboutPersonOut>[] = [
    {
      id: 'name',
      header: w.columnMember,
      cell: (person) => (
        <span className="flex min-w-0 flex-col gap-2">
          <span className="flex flex-wrap items-center gap-8">
            <span className="font-w600 text-text-strong">{person.display_name ?? ''}</span>
            {person.is_you ? (
              <Badge tone="neutral" testId="director-members-you">
                {w.you}
              </Badge>
            ) : null}
          </span>
          {person.is_you && ownTerm !== null ? (
            <span className="text-s12 text-text-tertiary" data-testid="director-members-own-term">
              {ownTerm}
            </span>
          ) : null}
        </span>
      ),
      stacked: 'heading',
    },
    {
      id: 'director',
      header: rw.columnDirector,
      cell: (person) => (
        <span className="text-text-secondary">
          {person.is_director ? rw.directorCellYes : rw.directorCellNo}
        </span>
      ),
      width: '22%',
    },
    {
      id: 'titles',
      header: rw.columnTitles,
      cell: (person) =>
        (person.title_labels ?? []).length > 0 ? (
          <span className="text-text-secondary">{(person.title_labels ?? []).join(', ')}</span>
        ) : (
          <span className="text-text-tertiary">—</span>
        ),
      width: '28%',
    },
  ];

  return (
    <DirectorShell
      title={w.membersTitle}
      description={w.membersLine}
      identity={directorBoardIdentity(board)}
      actions={
        <AllYourBoards
          me={me}
          settled={settled}
          committeeOnly={false}
          testId="director-members-back"
        />
      }
      testId="page-director-members"
    >
      <DirectorBoardContent orgRef={orgRef} notYours={notYours}>
        {about === null && aboutQuery.isPending && mayFire ? (
          <Skeleton className="h-84 w-full rounded-lg" testId="director-members-loading" />
        ) : null}
        <DirectorReadRefusal reads={[aboutQuery]} testId="director-members-read-step-up" />
        {aboutQuery.isError && !isSigningRefusal(aboutQuery.error) ? (
          <DirectorReadFailed
            read={aboutQuery}
            line="We couldn’t load these just now."
            testId="director-members-error"
            retryTestId="director-members-retry"
          />
        ) : null}

        {people.length > 0 ? (
          <Card className="overflow-hidden" testId="director-members-table">
            <DataTable
              columns={columns}
              rows={people}
              rowKey={(person) => person.board_member_id}
              rowTestId={(person) => `director-member-${person.board_member_id}`}
              stackAt="sm"
            />
            <p className="border-t border-border-subtle px-16 py-12 font-sans text-s12 font-w400 tracking-givmo text-text-tertiary">
              {rw.count(directors, people.length, 0)}
            </p>
          </Card>
        ) : null}

        {titles.length > 0 ? (
          <Card testId="director-members-duties">
            <CardBody className="flex flex-col gap-12">
              <h2 className="font-sans text-s16 font-w700 tracking-givmo text-text-strong">
                {w.whoDoesWhat}
              </h2>
              <dl className="flex flex-col gap-10">
                {titles.map((title) => (
                  <div
                    key={title.title_id ?? title.label}
                    className="flex flex-col gap-2 sm:flex-row sm:gap-16"
                  >
                    <dt className="w-[120px] shrink-0 font-sans text-s14 font-w600 tracking-givmo text-text-strong">
                      {title.label}
                    </dt>
                    <dd className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary">
                      {(title.acts ?? []).map(dutyWords).join(' · ')}
                    </dd>
                  </div>
                ))}
              </dl>
              {about?.duties?.adopted_at ? (
                <p className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-tertiary">
                  {gs.dutiesAdopted(displayLongDate(about.duties.adopted_at) ?? '')}
                </p>
              ) : null}
            </CardBody>
          </Card>
        ) : null}
      </DirectorBoardContent>
    </DirectorShell>
  );
}
